import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import GlobalSearch from '@/components/GlobalSearch';
import logoMark from '@/assets/logo-mark.png';
import { List, X, MagnifyingGlass, Sparkle, User, SignOut, GearSix, CalendarBlank, ChatCircle, ShieldCheck, EnvelopeSimple } from '@phosphor-icons/react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // desktop hamburger dropdown
  const [logoOk, setLogoOk] = useState(true);
  const menuRef = useRef(null);

  // Close the dropdown on outside click or Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const getDashboardLink = () => {
    if (!user) return '/auth';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'provider') return '/provider/dashboard';
    return '/dashboard';
  };

  const navLink = "text-sm font-medium text-muted-foreground hover:text-primary transition-colors";

  return (
    <nav className="sticky top-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="shrink-0 flex items-end" data-testid="logo-link" aria-label="Bookr.ai home">
            {logoOk ? (
              <img src={logoMark} alt="Bookr.ai" onError={() => setLogoOk(false)} className="h-[26px] w-auto mb-[3px]" />
            ) : (
              <span className="h-[26px] w-[26px] mb-[3px] rounded-lg bg-primary text-primary-foreground font-display text-lg font-bold flex items-center justify-center leading-none">B</span>
            )}
            <span className="font-display text-xl font-semibold text-foreground tracking-tight leading-none ml-px">ookr.ai</span>
          </Link>

          {/* Center: global search (desktop) */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <GlobalSearch />
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6 shrink-0">
            <Link to="/browse" className={navLink} data-testid="nav-browse">Browse</Link>
            {user && (
              <Link to="/ai-planner" className={`${navLink} flex items-center gap-1.5`} data-testid="nav-ai-planner">
                <Sparkle size={14} weight="fill" className="text-accent-coral" /> AI Planner
              </Link>
            )}
            {user && (
              <Link to="/coordinator" className={navLink} data-testid="nav-coordinator">Coordinator</Link>
            )}
            {user && (
              <Link to="/invitations" className={navLink} data-testid="nav-invitations">Invitations</Link>
            )}
            {user && (
              <Link to="/messages" className={navLink} data-testid="nav-messages">Messages</Link>
            )}
          </div>

          {/* Auth area */}
          <div className="hidden md:flex items-center gap-3 ml-6 shrink-0">
            {user ? (
              <>
                <Link to={getDashboardLink()} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-dashboard">
                  <User size={15} />
                  <span>{user.name?.split(' ')[0]}</span>
                </Link>
                <Link to="/profile" aria-label="Settings" className="text-muted-foreground/70 hover:text-foreground transition-colors" data-testid="nav-profile">
                  <GearSix size={17} />
                </Link>
                <button onClick={handleLogout} aria-label="Sign out" className="text-muted-foreground/70 hover:text-foreground transition-colors" data-testid="nav-logout">
                  <SignOut size={17} />
                </button>
              </>
            ) : (
              <>
                <Link to="/auth" className={navLink} data-testid="nav-login">Sign In</Link>
                <Link to="/auth?mode=register" className="bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold rounded-full btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90" data-testid="nav-register">Get Started</Link>
              </>
            )}
          </div>

          {/* Desktop hamburger dropdown menu */}
          <div className="hidden md:block relative ml-1" ref={menuRef}>
            <button onClick={() => setMenuOpen(o => !o)} aria-label="Menu" aria-expanded={menuOpen}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors cursor-pointer" data-testid="nav-menu-toggle">
              {menuOpen ? <X size={19} /> : <List size={19} />}
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-card border border-border/70 rounded-xl shadow-lift py-2 z-50 fade-up" role="menu" data-testid="nav-menu-dropdown">
                <Link to="/browse" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-browse"><MagnifyingGlass size={15} className="text-muted-foreground" /> Services</Link>
                {user && <Link to="/ai-planner" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-planner"><Sparkle size={15} weight="fill" className="text-accent-coral" /> AI Planner</Link>}
                {user && <Link to="/coordinator" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-coordinator"><CalendarBlank size={15} className="text-muted-foreground" /> Event Coordinator</Link>}
                {user && <Link to="/invitations" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-invitations"><EnvelopeSimple size={15} className="text-muted-foreground" /> Invitations</Link>}
                {user && <Link to="/messages" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-messages"><ChatCircle size={15} className="text-muted-foreground" /> Messages</Link>}
                <Link to="/trust" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-trust"><ShieldCheck size={15} className="text-muted-foreground" /> Trust &amp; Safety</Link>
                {user ? (
                  <>
                    <div className="my-1.5 border-t border-border/60" />
                    <Link to={getDashboardLink()} onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-dashboard"><User size={15} className="text-muted-foreground" /> Dashboard</Link>
                    <Link to="/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-settings"><GearSix size={15} className="text-muted-foreground" /> Settings</Link>
                    <button onClick={() => { setMenuOpen(false); handleLogout(); }} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors text-left cursor-pointer" data-testid="menu-signout"><SignOut size={15} className="text-muted-foreground" /> Sign out</button>
                  </>
                ) : (
                  <>
                    <div className="my-1.5 border-t border-border/60" />
                    <Link to="/auth" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-secondary/60 transition-colors" data-testid="menu-signin"><User size={15} className="text-muted-foreground" /> Sign In</Link>
                    <Link to="/auth?mode=register" onClick={() => setMenuOpen(false)} className="block mx-3 mt-1.5 text-center px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground rounded-lg" data-testid="menu-register">Get Started</Link>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Mobile: search + menu */}
          <div className="flex items-center gap-1 md:hidden">
            <button onClick={() => setSearchOpen(!searchOpen)} aria-label="Search" className="text-muted-foreground p-2.5" data-testid="mobile-search-toggle">
              <MagnifyingGlass size={20} />
            </button>
            <button onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu" className="text-foreground p-2.5" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile global search */}
        {searchOpen && (
          <div className="md:hidden pb-3 fade-up" data-testid="mobile-search">
            <GlobalSearch variant="mobile" autoFocus onNavigate={() => setSearchOpen(false)} />
          </div>
        )}

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border py-3 space-y-1 fade-up" data-testid="mobile-menu">
            <Link to="/browse" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary py-2.5 px-1">Services</Link>
            {user && <Link to="/ai-planner" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary py-2.5 px-1">AI Planner</Link>}
            {user && <Link to="/coordinator" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary py-2.5 px-1">Coordinator</Link>}
            {user && <Link to="/invitations" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary py-2.5 px-1">Invitations</Link>}
            {user && <Link to="/messages" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary py-2.5 px-1">Messages</Link>}
            {user ? (
              <>
                <Link to={getDashboardLink()} onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary py-2.5 px-1">Dashboard</Link>
                <Link to="/profile" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary py-2.5 px-1">Settings</Link>
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block w-full text-left text-sm font-medium text-muted-foreground py-2.5 px-1">Sign Out</button>
              </>
            ) : (
              <>
                <Link to="/auth" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary py-2.5 px-1">Sign In</Link>
                <Link to="/auth?mode=register" onClick={() => setMobileOpen(false)} className="block bg-primary text-primary-foreground px-4 py-3 text-sm text-center font-semibold rounded-full mt-2">Get Started</Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
